'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface PriceData {
  price: number;
  prevPrice: number;
  change24h?: number;
}

const DISPLAY_COINS = ['ETH'];

export function PriceTicker() {
  const { t } = useLanguage();
  const [prices, setPrices] = useState<Record<string, PriceData>>({});

  useEffect(() => {
    // 从服务器 API 获取价格数据
    const fetchPrices = async () => {
      try {
        const response = await fetch('/api/market');
        const result = await response.json();

        if (result.success && result.data) {
          setPrices(prev => {
            const updated: Record<string, PriceData> = {};

            DISPLAY_COINS.forEach(coin => {
              if (result.data[coin]) {
                const newPrice = parseFloat(result.data[coin]);
                const prevPrice = prev[coin]?.price || newPrice;

                updated[coin] = {
                  price: newPrice,
                  prevPrice,
                  change24h: prev[coin]?.change24h
                };
              }
            });

            return updated;
          });
        }
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    };

    // 立即执行
    fetchPrices();

    // 每15秒更新一次
    const interval = setInterval(fetchPrices, 15000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="relative border-b border-white/5 bg-black/65 backdrop-blur supports-[backdrop-filter]:bg-black/45">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 sm:w-12 bg-gradient-to-r from-[#050505] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 sm:w-12 bg-gradient-to-l from-[#050505] to-transparent" />

      <div className="relative mx-auto flex max-w-[1600px] gap-3 sm:gap-6 overflow-x-auto px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm scrollbar-hide">
        <AnimatePresence>
          {DISPLAY_COINS.map((coin) => {
            const data = prices[coin];
            if (!data) {
              return (
                <div key={coin} className="flex min-w-fit items-center gap-1 sm:gap-2 rounded-full border border-white/5 bg-white/5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs text-gray-400">
                  <span className="font-semibold tracking-wide text-gray-300">{coin}</span>
                  <span className="font-mono text-[9px] sm:text-[11px] uppercase text-gray-400">{t.priceTicker.loading}</span>
                </div>
              );
            }

            const isUp = data.price > data.prevPrice;
            const isDown = data.price < data.prevPrice;

            return (
              <motion.div
                key={coin}
                className="flex min-w-fit items-center gap-1 sm:gap-2 rounded-full border border-white/10 bg-white/[0.08] px-2 sm:px-4 py-1 sm:py-1.5 shadow-inner"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  backgroundColor: isUp
                    ? 'rgba(34, 197, 94, 0.15)'
                    : isDown
                    ? 'rgba(239, 68, 68, 0.15)'
                    : 'rgba(255, 255, 255, 0.08)'
                }}
                transition={{ duration: 0.3 }}
              >
                <span className="font-semibold tracking-wide text-gray-200 text-xs sm:text-sm">
                  {coin}
                </span>

                <motion.span
                  className="font-mono text-xs sm:text-base text-white"
                  animate={{
                    scale: isUp || isDown ? [1, 1.05, 1] : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  ${data.price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </motion.span>

                {isUp && (
                  <span className="text-[9px] sm:text-xs text-emerald-400">▲</span>
                )}
                {isDown && (
                  <span className="text-[9px] sm:text-xs text-rose-400">▼</span>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
